import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import apiService from '../services/apiService';
import { PERIOD_OPTIONS } from './PeriodSelector';
import './ACChargingChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ACChargingChart = ({ selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month }) => {
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('sessions'); // 'sessions' or 'timeline'

  const loadData = useCallback(async (period, year, month, year12, month12) => {
    try {
      setLoading(true);
      setError(null);
      
      const periodConfig = PERIOD_OPTIONS.find(p => p.id === period);
      let response;
      if (period === 'select') {
        response = await apiService.getAcChargerEvents(null, year, month);
      } else if (period === '12months') {
        response = await apiService.getAcChargerEvents(12, year12, month12);
      } else {
        response = await apiService.getAcChargerEvents(periodConfig.months);
      }
      
      setData(response);
    } catch (err) {
      console.error('Failed to load AC charger data:', err);
      setError(err.message || 'Failed to load AC charger data');
    } finally {
      setLoading(false);
    }
  }, []);

  const buildChartData = useCallback((response, period) => {
    if (!response?.dailySummary || response.dailySummary.length === 0) {
      setChartData(null);
      return;
    }

    const useMonthYear = period === 'all' || period === '12months';
    
    const labels = response.dailySummary.map(d => {
      const date = new Date(d.day);
      if (useMonthYear) {
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date.getFullYear().toString().slice(-2);
        return `${month} ${year}`;
      } else {
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const day = date.getDate();
        return `${month} ${day}`;
      }
    });

    const dataPoints = response.dailySummary.length;
    
    setChartData({
      labels,
      datasets: [
        {
          label: 'Charger ON Events',
          data: response.dailySummary.map(d => d.onCount),
          backgroundColor: 'rgba(251, 191, 36, 0.8)',
          borderColor: 'rgb(251, 191, 36)',
          borderWidth: 1,
          barThickness: dataPoints > 60 ? 2 : dataPoints > 30 ? 4 : 8
        },
        {
          label: 'Charger OFF Events',
          data: response.dailySummary.map(d => d.offCount),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 1,
          barThickness: dataPoints > 60 ? 2 : dataPoints > 30 ? 4 : 8
        }
      ]
    });
  }, []);

  useEffect(() => {
    loadData(selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month);
  }, [selectedPeriod, selectedYear, selectedMonth, selected12Year, selected12Month, loadData]);

  useEffect(() => {
    if (data) {
      buildChartData(data, selectedPeriod);
    }
  }, [data, selectedPeriod, buildChartData]);

  const getOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'rgba(255, 255, 255, 0.8)',
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          stepSize: 1
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        title: {
          display: true,
          text: 'Events',
          color: 'rgba(255, 255, 255, 0.6)'
        }
      }
    }
  });

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const formatTime = (isoString) => {
    const d = new Date(isoString);
    return d.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="ac-charging-chart">
      <div className="chart-header">
        <h3>AC Charger</h3>
        <div className="view-toggle">
          <button
            className={`view-btn ${viewMode === 'sessions' ? 'active' : ''}`}
            onClick={() => setViewMode('sessions')}
          >
            Sessions
          </button>
          <button
            className={`view-btn ${viewMode === 'timeline' ? 'active' : ''}`}
            onClick={() => setViewMode('timeline')}
          >
            Timeline
          </button>
        </div>
      </div>

      {data && (
        <div className="ac-stats">
          <div className="stat-item">
            <span className="stat-label">Total Sessions</span>
            <span className="stat-value">{data.totalSessions}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Charging Time</span>
            <span className="stat-value">{data.totalChargingHours}h</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Events</span>
            <span className="stat-value">{data.totalEvents}</span>
          </div>
        </div>
      )}

      <div className="chart-container">
        {loading ? (
          <div className="chart-loading">Loading AC charger data...</div>
        ) : error ? (
          <div className="chart-error">{error}</div>
        ) : viewMode === 'timeline' && chartData ? (
          <Bar options={getOptions()} data={chartData} />
        ) : viewMode === 'sessions' && data?.sessions ? (
          <div className="sessions-list">
            {data.sessions.length === 0 ? (
              <div className="no-sessions">No charging sessions in this period</div>
            ) : (
              data.sessions.slice().reverse().map((session, idx) => (
                <div key={idx} className={`session-card ${session.ongoing ? 'ongoing' : ''}`}>
                  <div className="session-header">
                    <span className="session-time">
                      {formatTime(session.start)}
                      {session.end && ` → ${formatTime(session.end)}`}
                      {session.ongoing && <span className="ongoing-badge">CHARGING</span>}
                    </span>
                    <span className="session-duration">{formatDuration(session.durationMinutes)}</span>
                  </div>
                  <div className="session-details">
                    <span className="detail">
                      <span className="label">Start:</span> 
                      {session.startVoltage}V / {session.startPercent}%
                    </span>
                    {session.endVoltage && (
                      <span className="detail">
                        <span className="label">End:</span> 
                        {session.endVoltage}V / {session.endPercent}%
                      </span>
                    )}
                  </div>
                  {session.reason && (
                    <div className="session-reason">{session.reason}</div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="chart-error">No data available</div>
        )}
      </div>
    </div>
  );
};

export default ACChargingChart;
